import { createSignal, createEffect, createRoot } from 'solid-js';
import { isServer } from 'solid-js/web';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@lib/supabase/client';

export type MedicalStatus = 'none' | 'pending' | 'approved' | 'rejected';

export type AppProfile = {
  id: string;
  username: string | null;
  is_public: boolean;
  opt_in_terp_profile: boolean;
  accepted_disclaimer_at: string | null;
  // Migration 0013 adds this column. Optional here because older sessions
  // (or dev DBs that haven't run the migration) will just be missing it;
  // treat absence as 'patient'.
  // Migration 0033 adds 'visitor' as the new default. Existing dev DBs
  // that haven't migrated yet still return 'patient' for everyone, so
  // the field remains optional for backwards compat.
  role?: 'visitor' | 'patient' | 'moderator' | 'admin';
  // Migration 0016 — medical patient verification.
  medical_status?: MedicalStatus;
  clinic_name?: string | null;
  // Migration 0045 — drives the Prescription Planner's pricing
  // preference. Null means "no preferred pharmacy"; the planner falls
  // back to cheapest-in-stock in that case.
  default_pharmacy_slug?: string | null;
};

const PROFILE_COLUMNS =
  'id, username, is_public, opt_in_terp_profile, accepted_disclaimer_at, role, medical_status, clinic_name, default_pharmacy_slug';

// Module-scope Solid graph nodes must live inside a createRoot — otherwise
// createEffect logs a "computations created outside a createRoot" warning
// because there is no disposer to clean them up on HMR reload. This root
// lives for the lifetime of the app, which is exactly what we want for an
// auth store.
const { session, user, profile, setProfile, loading } = createRoot(() => {
  const [session, setSession] = createSignal<Session | null>(null);
  // Custom equality for the user signal: Supabase Auth fires
  // `TOKEN_REFRESHED` whenever a tab regains focus, and the callback
  // hands us a NEW `User` object reference even though the actual
  // identity is unchanged. With default reference equality, that flips
  // the signal, reruns every dependent effect (profile fetch, route
  // resources keyed on `authStore.user()`), and produces a brief
  // "Loading…" flash on tab-switch. Comparing by `id` collapses those
  // no-op refreshes so downstream consumers only react when the user
  // actually changes (sign-in / sign-out).
  const [user, setUser] = createSignal<User | null>(null, {
    equals: (prev, next) => (prev?.id ?? null) === (next?.id ?? null),
  });
  const [profile, setProfile] = createSignal<AppProfile | null>(null);
  // During SSR there is no auth session — start as "not loading" on the
  // server so the initial HTML renders the signed-out view deterministically.
  // Client hydration flips this back to `true` for a moment while we
  // recover the persisted session from localStorage.
  const [loading, setLoading] = createSignal(!isServer);

  if (!isServer) {
    // Keep the session signal in lockstep with Supabase Auth. Any route guard
    // that reads `user()` will re-run automatically when the user signs in/out.
    // Gated on the client because Supabase Auth touches localStorage and
    // window — both absent during SSR.
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (!newSession) setProfile(null);
    });
  }

  // Anonymous-by-design profile bootstrap:
  // - On first sign-in we materialise a profile row keyed by auth.users.id.
  // - The profile's own UUID (`id`) — not the auth UUID — is the handle
  //   used by sessions/watchlist/etc., per the Privacy by Design rule.
  createEffect(() => {
    if (isServer) return;
    const u = user();
    if (!u) return;
    void ensureProfile(u.id).then(setProfile);
  });

  return { session, user, profile, setProfile, loading };
});

async function ensureProfile(authUserId: string): Promise<AppProfile | null> {
  const existing = await supabase
    .from('profiles')
    .select(PROFILE_COLUMNS)
    .eq('user_id', authUserId)
    .maybeSingle();

  if (existing.data) return existing.data as unknown as AppProfile;

  const inserted = await supabase
    .from('profiles')
    .insert({ user_id: authUserId })
    .select(PROFILE_COLUMNS)
    .single();

  return (inserted.data as unknown as AppProfile) ?? null;
}

function siteOrigin(): string | undefined {
  if (isServer) return undefined;
  // VITE_SITE_URL is the canonical public origin (e.g. https://terptrack.vercel.app).
  // On Vercel, set it per-environment; locally it falls back to window.location.origin
  // so magic links still work when testing against a dev server.
  // NOTE: also add both URLs to Supabase → Auth → URL Configuration → Redirect URLs,
  // and set Site URL to the production VITE_SITE_URL value.
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  return envUrl || window.location.origin;
}

export const authStore = {
  session,
  user,
  profile,
  loading,
  isAdmin: () => profile()?.role === 'admin',
  isModeratorOrAdmin: () => {
    const r = profile()?.role;
    return r === 'admin' || r === 'moderator';
  },
  isMedicalPatient: () => profile()?.medical_status === 'approved',
  medicalStatus: (): MedicalStatus => profile()?.medical_status ?? 'none',
  async signInWithMagicLink(email: string, returnPath?: string) {
    const origin = siteOrigin();
    // If the caller passed a relative return path (from a `?return=`
    // handoff such as /shared-script's "Sign in to import" button),
    // append it to the base origin so the magic-link landing page IS
    // the destination — no AppShell glue or post-auth navigation
    // hook required. We tolerate an absent leading slash; same-origin
    // only is enforced by the relative-prefix check (anything that
    // looks like an external URL is silently dropped).
    let dest = origin;
    if (returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')) {
      dest = `${origin}${returnPath}`;
    }
    return supabase.auth.signInWithOtp({
      email,
      options: {
        // Supabase client has detectSessionInUrl:true, so any page in the app
        // will parse the hash on load — we just need to point the link at the
        // canonical prod origin (or origin+returnPath), not whatever
        // window.location.origin happened to be when the request was issued.
        emailRedirectTo: dest,
      },
    });
  },
  async submitClinic(clinicName: string) {
    const p = profile();
    if (!p) throw new Error('Not signed in');
    const trimmed = clinicName.trim();
    if (!trimmed) throw new Error('Clinic name required');
    // Trigger will uppercase + whitelist-check + auto-approve server-side.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabase as any;
    const { data, error } = await db
      .from('profiles')
      .update({ clinic_name: trimmed, medical_status: 'pending' })
      .eq('id', p.id)
      .select(PROFILE_COLUMNS)
      .single();
    if (error) throw error;
    if (data) setProfile(data as unknown as AppProfile);
    return data as unknown as AppProfile;
  },
  async refreshProfile() {
    const u = user();
    if (!u) return;
    const fresh = await ensureProfile(u.id);
    if (fresh) setProfile(fresh);
  },
  async signOut() {
    await supabase.auth.signOut();
    setProfile(null);
  },
  async acceptDisclaimer() {
    const p = profile();
    if (!p) return;
    const { data } = await supabase
      .from('profiles')
      .update({ accepted_disclaimer_at: new Date().toISOString() })
      .eq('id', p.id)
      .select(PROFILE_COLUMNS)
      .single();
    if (data) setProfile(data as unknown as AppProfile);
  },
};
